import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2, TriangleAlert } from "lucide-react";

import Loading from "@/components/Loading";
import { RelativeDateCell } from "@/components/RelativeDateCell";
import { formatDateTimeCompact, formatRelativeDateTime } from "@/utils/relative-time";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import { getColumnSortAria, SortableColumnHeader } from "@/components/ui/sortable-column-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { type Manager } from "../../types";

interface ListProps {
  isFetching: boolean;
  errorMessage?: string;
  managers: Manager[];
  onClickView: (manager: Manager) => void;
  onClickDelete: (manager: Manager) => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

const PAGE_SIZE = 20;

const EMPTY_VALUE = "-";

export const DEFAULT_SORTING: SortingState = [{ id: "registeredOn", desc: true }];

export const MANAGER_SORT_OPTIONS = [
  { id: "registeredOn", translationKey: "column.registeredOn" },
  { id: "firstName", translationKey: "column.managerFirstName" },
  { id: "lastName", translationKey: "column.managerLastName" },
  { id: "email", translationKey: "column.wfpEmail" },
  { id: "countries", translationKey: "column.countries" },
  { id: "lastLogin", translationKey: "column.lastLogin" },
] as const;

const formatManagerDateTime = (date?: string | null) => formatDateTimeCompact(date) || EMPTY_VALUE;

const createColumns = (
  t: TFunction,
  onClickView: (manager: Manager) => void,
  onClickDelete: (manager: Manager) => void
): ColumnDef<Manager, unknown>[] => [
  {
    id: "registeredOn",
    accessorFn: manager => manager.registered_on ?? undefined,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("column.registeredOn")} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatManagerDateTime(row.original.registered_on)}
      </span>
    ),
    sortingFn: "basic",
    sortUndefined: "last",
  },
  {
    id: "firstName",
    accessorKey: "first_name",
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("column.managerFirstName")} />
    ),
    cell: ({ row }) => (
      <span className="font-medium text-foreground">{row.original.first_name}</span>
    ),
  },
  {
    id: "lastName",
    accessorKey: "last_name",
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("column.managerLastName")} />
    ),
  },
  {
    id: "email",
    accessorKey: "email",
    header: ({ column }) => <SortableColumnHeader column={column} label={t("column.wfpEmail")} />,
  },
  {
    id: "countries",
    accessorKey: "countriesString",
    header: ({ column }) => <SortableColumnHeader column={column} label={t("column.countries")} />,
  },
  {
    id: "lastLogin",
    accessorFn: manager => manager.last_login ?? undefined,
    header: ({ column }) => <SortableColumnHeader column={column} label={t("column.lastLogin")} />,
    cell: ({ row }) => (
      <RelativeDateCell
        relative={formatRelativeDateTime(row.original.last_login, t)}
        absolute={formatDateTimeCompact(row.original.last_login)}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
  },
  {
    id: "actions",
    header: () => <span className="sr-only">{t("common.edit")}</span>,
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex items-center justify-end gap-2">
        <WFPButton
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onClickView(row.original)}
        >
          <Pencil className="size-4" aria-hidden="true" />
          {t("common.edit")}
        </WFPButton>
        <WFPButton
          type="button"
          variant="destructive-secondary"
          size="sm"
          onClick={() => onClickDelete(row.original)}
        >
          <Trash2 className="size-4" aria-hidden="true" />
          {t("common.delete")}
        </WFPButton>
      </div>
    ),
  },
];

export const List = ({
  isFetching,
  errorMessage = "",
  managers,
  onClickView,
  onClickDelete,
  sorting,
  onSortingChange,
}: ListProps) => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const columns = useMemo(
    () => createColumns(t, onClickView, onClickDelete),
    [onClickDelete, onClickView, t]
  );

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(1, Math.ceil(managers.length / currentPagination.pageSize));
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [managers.length]);

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  }, [sorting]);

  const table = useReactTable({
    columns,
    data: managers,
    autoResetPageIndex: false,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: manager => String(manager.id),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange,
    state: { pagination, sorting },
  });

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <TriangleAlert />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHead key={header.id} aria-sort={getColumnSortAria(header.column)}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Loading isVisible small />
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
                {t("managers.empty")}
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching
            ? rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id} className="p-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
        </TableBody>
      </Table>
      {managers.length > 0 ? <DataTablePagination table={table} /> : null}
    </div>
  );
};
